// src/utils/printSizes.js
//
// Page sizes in the print intake: which print size a book or a PDF already is,
// so the order can start at it, and what the author should hear when the order
// and the book disagree. Pure; the PDF itself is read by utils/pdfInfo.js.
//
// The sizes come from config/bookSizes.js, the table the editor and the book
// printer use, through each size's `quote` alias, the print-size option id.

import { BOOK_SIZES_BY_ID, DEFAULT_BOOK_SIZE, fromQuoteSelection } from '../config/bookSizes';

// A print-ready PDF may carry bleed past the trim: 0.125 in on the outside
// edge, top and bottom (+0.125 wide, +0.25 tall), or on all four sides
// (+0.25 both ways). Each still counts as its trim size.
const BLEEDS = [
  [0, 0],
  [0.125, 0.25],
  [0.25, 0.25],
];
const TOLERANCE_IN = 0.03;

/** "6 × 9 in", without trailing zeros. */
export function inches(widthIn, heightIn) {
  const trim = (n) => String(Math.round(n * 100) / 100);
  return `${trim(widthIn)} × ${trim(heightIn)} in`;
}

/** The size behind a print-size option id, or null ("custom" has none). */
export function sizeForOption(optionId) {
  const id = fromQuoteSelection(optionId);
  return id ? BOOK_SIZES_BY_ID[id] : null;
}

/** Whether a page of widthIn × heightIn is the print size `optionId`, trimmed or with bleed. */
export function pageFits(widthIn, heightIn, optionId) {
  const size = sizeForOption(optionId);
  if (!size) return false;
  return BLEEDS.some(
    ([bw, bh]) =>
      Math.abs(widthIn - (size.widthIn + bw)) <= TOLERANCE_IN &&
      Math.abs(heightIn - (size.heightIn + bh)) <= TOLERANCE_IN
  );
}

/** The size options the live pricing offers, in its order. */
export function offeredSizes(pricing) {
  const cat = ((pricing && pricing.categories) || []).find((c) => c.id === 'size');
  return ((cat && cat.options) || []).map((o) => o.id);
}

/** The option's name as the author sees it ("1/2 Letter (5.5" x 8.5")"), or the id. */
export function optionName(pricing, categoryId, optionId) {
  const cat = ((pricing && pricing.categories) || []).find((c) => c.id === categoryId);
  const option = ((cat && cat.options) || []).find((o) => o.id === optionId);
  return option ? option.name : optionId;
}

/**
 * The size a ReVisionari book is laid out at, as a book-size id. The same answer
 * the editor and the book printer give (trim_size_for_draft): its own trim size,
 * else the size it was quoted at on /quote, else the default.
 */
export function bookSizeId(draft) {
  if (draft && BOOK_SIZES_BY_ID[draft.trimSize]) return draft.trimSize;
  const quoted =
    draft && draft.print_spec && draft.print_spec.selections
      ? fromQuoteSelection(draft.print_spec.selections.size)
      : null;
  return quoted || DEFAULT_BOOK_SIZE;
}

/** That size as a print-size option id, or null when it is a size we don't print. */
export function bookPrintOption(draft) {
  const size = BOOK_SIZES_BY_ID[bookSizeId(draft)];
  return (size && size.aliases && size.aliases.quote) || null;
}

/** "Trade (6 × 9 in)". */
export function bookSizeLabel(draft) {
  const size = BOOK_SIZES_BY_ID[bookSizeId(draft)];
  return `${size.label} (${inches(size.widthIn, size.heightIn)})`;
}

/**
 * The cover an uploaded book needs, in words, and what is wrong with the one
 * the author chose. `need` is GET /api/intake/cover-size's `cover` (null when
 * only the team can say); `cover` is what the browser read from the file: a
 * PDF's { widthIn, heightIn }, or a picture's { widthPx, heightPx }.
 * Returns { guide, notes }: the size to aim for, and warnings.
 */
export function coverAdvice({ need, cover, specs }) {
  if (!need) {
    const hardcover = specs && (specs.cover_type === 'hardcover' || specs.cover_type === 'hardcover_jacket');
    return {
      guide: hardcover ? 'For a hardcover, our team confirms the exact cover size with you during review.' : null,
      notes: [],
    };
  }
  const size = inches(need.width_in, need.height_in);
  const spine = Math.round(need.spine_in * 100) / 100;
  let guide;
  if (need.layout === 'separate') {
    guide = `Your cover should be a front and a back, each ${size}, with 0.125 in bleed on every side.`;
  } else if (need.spine_in > 0) {
    guide = `Your cover should be one file of ${size}: back, spine (about ${spine} in) and front, with 0.125 in bleed on every side.`;
  } else {
    guide = `Your cover should be one file of ${size}: back and front with no spine, because this book is folded and stapled.`;
  }

  const notes = [];
  if (cover && cover.widthIn) {
    if (Math.abs(cover.widthIn - need.width_in) > 0.15 || Math.abs(cover.heightIn - need.height_in) > 0.15) {
      notes.push({ text: `Your cover file is ${inches(cover.widthIn, cover.heightIn)}, but this book needs ${size}.` });
    }
  } else if (cover && cover.widthPx && cover.heightPx) {
    const shape = cover.widthPx / cover.heightPx;
    if (need.layout === 'wrap' && Math.abs(shape - need.width_in / need.height_in) > 0.08) {
      notes.push({
        text: "Your cover picture's shape doesn't match this book's cover. It may be the front only: we need the whole cover in one file.",
      });
    }
    const dpi = cover.widthPx / need.width_in;
    if (dpi < 250) {
      notes.push({
        text: `Your cover picture is about ${Math.round(dpi)} pixels per inch at print size. For sharp print it needs 300, which is ${Math.round(need.width_in * 300)} pixels wide.`,
      });
    }
  }
  return { guide, notes };
}

/**
 * What the author should know about their specs before submitting, as plain
 * notes. A note may carry a one-click fix: `{ text, fix: { label, specs } }`.
 *
 * `pdf` is what utils/pdfInfo.js read from an uploaded PDF manuscript;
 * `bookOption` is a ReVisionari book's own print size (bookPrintOption).
 * Nothing here blocks a submission: the team still checks every book, and a
 * PDF with crop marks can read as the wrong size without being wrong.
 */
export function specsNotes({ source, specs, pdf, bookOption, pricing }) {
  const notes = [];
  if (!specs) return notes;
  const name = (id) => optionName(pricing, 'size', id);
  const pagesCat = ((pricing && pricing.categories) || []).find((c) => c.id === 'pages');

  if (source === 'upload' && pdf && pdf.status === 'ok') {
    if (pdf.mixed) {
      notes.push({ text: "Your PDF's pages aren't all the same size. Our team will check it before printing." });
    }
    // No size on the order (a pricing without sizes) or a custom one: nothing to compare.
    if (specs.size && specs.size !== 'custom' && !pageFits(pdf.widthIn, pdf.heightIn, specs.size)) {
      const match = offeredSizes(pricing).find((id) => pageFits(pdf.widthIn, pdf.heightIn, id));
      notes.push(
        match
          ? {
              text: `Your PDF's pages are ${inches(pdf.widthIn, pdf.heightIn)}, but this order is for ${name(specs.size)}.`,
              fix: { label: `Use ${name(match)}`, specs: { ...specs, size: match } },
            }
          : {
              text: `Your PDF's pages are ${inches(pdf.widthIn, pdf.heightIn)}, which isn't one of our print sizes. Upload a PDF at one of our sizes, or choose Custom Size and our team will check it.`,
            }
      );
    }
    if (pdf.pages && specs.pages !== pdf.pages) {
      const inRange = !pagesCat || (pdf.pages >= pagesCat.min && pdf.pages <= pagesCat.max);
      notes.push(
        inRange
          ? {
              text: `Your PDF has ${pdf.pages} pages, but the specs say ${specs.pages}.`,
              fix: { label: `Use ${pdf.pages}`, specs: { ...specs, pages: pdf.pages } },
            }
          : {
              text: `Your PDF has ${pdf.pages} pages. We print books of ${pagesCat.min} to ${pagesCat.max} pages.`,
            }
      );
    }
  }

  if (source === 'revisionari' && bookOption && specs.size && specs.size !== bookOption) {
    notes.push({
      text: `Your book is laid out at ${name(bookOption)}. Printing it at ${name(specs.size)} moves the page breaks, so it won't match what you saw in the preview.`,
      fix: { label: `Use ${name(bookOption)}`, specs: { ...specs, size: bookOption } },
    });
  }

  if (specs.binding === 'saddle_stitch' && specs.pages % 4 !== 0) {
    notes.push({
      text: `Saddle stitch needs a page count in multiples of 4. ${specs.pages} pages will be rounded up with blank pages.`,
    });
  }
  return notes;
}
